'use client';
import React, { Fragment, useEffect, useState, useRef } from 'react';
import CardTour from '@/components/front/ui/CardTour';
import TitleAttrSingle from '@/components/front/ui/TitleAttrSingle';
import axios from '@/lib/axios';
import { ApiRoutes } from '@/settings/routes';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import LoadingFront from './LoadingFront';
import { App_text } from '@/settings/AppText';
import CardCityBreak from './CardCityBreak';

export default function Visited({ data, type }) {
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const [tours, setTours] = useState([]);
    const [activities, setActivities] = useState([]);
    const [cityBreaks, setCityBreaks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let newDataId = data.id;

        // Retrieve stored ids from local storage
        const storedTourIds = JSON.parse(localStorage.getItem('tourIds')) || [];
        const storedActivityIds = JSON.parse(localStorage.getItem('activityIds')) || [];
        const storedCityBreakIds = JSON.parse(localStorage.getItem('cityBreakIds')) || [];

        if (type === 'tours') {
            // Check if the id already exists in the tour ids array
            if (!storedTourIds.includes(newDataId)) {
                // Add new id to the tour ids array and save to local storage
                storedTourIds.push(newDataId);
                localStorage.setItem('tourIds', JSON.stringify(storedTourIds));
            }
        } else if (type === 'activities') {
            // Check if the id already exists in the activity ids array
            if (!storedActivityIds.includes(newDataId)) {
                // Add new id to the activity ids array and save to local storage
                storedActivityIds.push(newDataId);
                localStorage.setItem('activityIds', JSON.stringify(storedActivityIds));
            }
        } else if (type === 'city_breaks') {
            // Check if the id already exists in the city break ids array
            if (!storedCityBreakIds.includes(newDataId)) {
                // Add new id to the city break ids array and save to local storage
                storedCityBreakIds.push(newDataId);
                localStorage.setItem('cityBreakIds', JSON.stringify(storedCityBreakIds));
            }
        }
    }, [data, type]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const storedTourIds = JSON.parse(localStorage.getItem('tourIds')) || [];
                const storedActivityIds = JSON.parse(localStorage.getItem('activityIds')) || [];
                const storedCityBreakIds = JSON.parse(localStorage.getItem('cityBreakIds')) || [];

                if (storedTourIds.length > 0 && type === 'tours') {
                    const tourResponse = await axios.post(ApiRoutes.API_get_visited_tours, { ids: storedTourIds, except: data.id });
                    setTours(tourResponse.data.tours);
                }

                if (storedActivityIds.length > 0 && type === 'activities') {
                    const activityResponse = await axios.post(ApiRoutes.API_get_visited_activities, { ids: storedActivityIds, except: data.id });
                    setActivities(activityResponse.data.activities);
                }

                if (storedCityBreakIds.length > 0 && type === 'city_breaks') {
                    const cityBreakResponse = await axios.post(ApiRoutes.API_get_visited_city_breaks, { ids: storedCityBreakIds, except: data.id });
                    setCityBreaks(cityBreakResponse.data.city_breaks);
                }

                setLoading(false);
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return <LoadingFront />;
    }

    return (
        <div id="recent-viewed">
            {type === 'tours' && tours.length !== 0 && (
                <div className="relative flex flex-col gap-8">
                    <hr className="hidden mt-4 lg:block" />
                    <div className="flex flex-col items-center justify-between md:flex-row">
                        <div className="default-color-title custom-style-h2">{App_text.generale.recently_viewd_title_tours}</div>
                        <div className="relative items-center hidden gap-2 mb-4 lg:flex md:mb-0 ">
                            <span ref={navigationPrevRef} className="p-3 text-gray-600 transition-colors bg-white border rounded-full cursor-pointer hover:bg-main-color hover:text-white">
                                <FaAngleLeft className="w-4 h-4" />
                            </span>
                            <span ref={navigationNextRef} className="p-3 text-gray-600 transition-colors bg-white border rounded-full cursor-pointer hover:bg-main-color hover:text-white">
                                <FaAngleRight className="w-4 h-4" />
                            </span>
                        </div>
                    </div>
                    <Swiper
                        slidesPerView={1}
                        breakpoints={{
                            640: {
                                slidesPerView: 2,
                            },
                            1300: {
                                slidesPerView: 3,
                            },
                        }}
                        spaceBetween={10}
                        autoplay={{
                            delay: 3500,
                            disableOnInteraction: false,
                        }}
                        modules={[Autoplay, Navigation]}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        onBeforeInit={(swiper) => {
                            swiper.params.navigation.prevEl = navigationPrevRef.current;
                            swiper.params.navigation.nextEl = navigationNextRef.current;
                        }}
                        className="promotion-swiper"
                    >
                        {tours.map((tour, index) => (
                            <SwiperSlide key={index}>
                                <div className="px-1 py-4 lg:px-3">
                                    <CardTour data={tour} postType={'tours'} />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}

            {type === 'activities' && activities.length !== 0 && (
                <div className="relative flex flex-col gap-8">
                    <hr className="hidden mt-4 lg:block" />
                    <div className="flex flex-col items-center justify-between md:flex-row">
                        <div className="default-color-title custom-style-h2">{App_text.generale.recently_viewd_title_excursions}</div>
                        <div className="relative items-center hidden gap-2 mb-4 lg:flex md:mb-0 ">
                            <span ref={navigationPrevRef} className="p-3 text-gray-600 transition-colors bg-white border rounded-full cursor-pointer hover:bg-main-color hover:text-white">
                                <FaAngleLeft className="w-4 h-4" />
                            </span>
                            <span ref={navigationNextRef} className="p-3 text-gray-600 transition-colors bg-white border rounded-full cursor-pointer hover:bg-main-color hover:text-white">
                                <FaAngleRight className="w-4 h-4" />
                            </span>
                        </div>
                    </div>
                    <Swiper
                        slidesPerView={1}
                        breakpoints={{
                            640: {
                                slidesPerView: 2,
                            },
                            1300: {
                                slidesPerView: 3,
                            },
                        }}
                        spaceBetween={10}
                        autoplay={{
                            delay: 3500,
                            disableOnInteraction: false,
                        }}
                        modules={[Autoplay, Navigation]}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        onBeforeInit={(swiper) => {
                            swiper.params.navigation.prevEl = navigationPrevRef.current;
                            swiper.params.navigation.nextEl = navigationNextRef.current;
                        }}
                        className="promotion-swiper"
                    >
                        {activities.map((activity, index) => (
                            <SwiperSlide key={index}>
                                <div className="px-1 py-4 lg:px-3">
                                    <CardTour data={activity} postType={'activities'} />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}

            {type === 'city_breaks' && cityBreaks.length !== 0 && (
                <div className="relative flex flex-col gap-8">
                    <hr className="hidden mt-4 lg:block" />
                    <div className="flex flex-col items-center justify-between md:flex-row">
                        <div className="default-color-title custom-style-h2">{App_text.generale.recently_viewd_title_city_breaks || 'Recently Viewed City Breaks'}</div>
                        <div className="relative items-center hidden gap-2 mb-4 lg:flex md:mb-0 ">
                            <span ref={navigationPrevRef} className="p-3 text-gray-600 transition-colors bg-white border rounded-full cursor-pointer hover:bg-main-color hover:text-white">
                                <FaAngleLeft className="w-4 h-4" />
                            </span>
                            <span ref={navigationNextRef} className="p-3 text-gray-600 transition-colors bg-white border rounded-full cursor-pointer hover:bg-main-color hover:text-white">
                                <FaAngleRight className="w-4 h-4" />
                            </span>
                        </div>
                    </div>
                    <Swiper
                        slidesPerView={1}
                        breakpoints={{
                            640: {
                                slidesPerView: 2,
                            },
                            1300: {
                                slidesPerView: 3,
                            },
                        }}
                        spaceBetween={10}
                        autoplay={{
                            delay: 3500,
                            disableOnInteraction: false,
                        }}
                        modules={[Autoplay, Navigation]}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        onBeforeInit={(swiper) => {
                            swiper.params.navigation.prevEl = navigationPrevRef.current;
                            swiper.params.navigation.nextEl = navigationNextRef.current;
                        }}
                        className="promotion-swiper"
                    >
                        {cityBreaks.map((cityBreak, index) => (
                            <SwiperSlide key={index}>
                                <div className="px-1 py-4 lg:px-3">
                                    <CardCityBreak data={cityBreak} postType={'city_breaks'} />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            )}
        </div>
    );
}
